/**
 * Financial Integrity Audit — UI panel
 * =====================================
 * Surfaces the read-only Financial Integrity Audit Engine
 * (services/financial_integrity_audit.py) inside the SSOT Audit page.
 *
 * Shows:
 *   • Health summary tiles (issues, critical, ₹ impacts)
 *   • Findings table — one row per check that tripped, grouped by category
 *   • Severity filter + drill-down dialog with the affected IDs
 *
 * Read-only. Auto-fix hints come back from the engine — operator chooses
 * whether to act.
 *
 * Reference: /app/memory/REVENUE_RECOGNITION_RULES.md
 * Reference: /app/memory/FINANCIAL_INTEGRITY_AUDIT_RESULTS.md
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { RefreshCw, AlertOctagon, AlertTriangle, Info, ShieldCheck, ListChecks } from 'lucide-react';

const SEVERITY_STYLES = {
  critical: { cls: 'bg-rose-50 text-rose-700 border-rose-200', icon: AlertOctagon },
  high:     { cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: AlertTriangle },
  medium:   { cls: 'bg-yellow-50 text-yellow-700 border-yellow-200', icon: AlertTriangle },
  low:      { cls: 'bg-sky-50 text-sky-700 border-sky-200', icon: Info },
};

const SEVERITY_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };

const formatRupees = (v) => {
  const n = Number(v || 0);
  if (n === 0) return '—';
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const SeverityBadge = ({ severity }) => {
  const s = SEVERITY_STYLES[severity] || SEVERITY_STYLES.low;
  const Icon = s.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${s.cls}`}
      data-testid={`severity-badge-${severity}`}
    >
      <Icon className="h-3 w-3" />
      {severity}
    </span>
  );
};

const SummaryTile = ({ label, value, accent, testid }) => (
  <Card data-testid={testid}>
    <CardHeader className="pb-2">
      <CardDescription>{label}</CardDescription>
      <CardTitle className={`text-3xl ${accent || ''}`}>{value}</CardTitle>
    </CardHeader>
  </Card>
);

export default function AdminFinancialIntegrityPanel() {
  const { apiCall } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [severityFilter, setSeverityFilter] = useState('all'); // all | critical | high | medium | low | issues
  const [drilldown, setDrilldown] = useState(null);

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiCall('/api/admin/financial-integrity/run');
      setReport(data);
    } catch (e) {
      toast.error('Failed to load Financial Integrity audit');
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => {
    fetchAudit();
  }, [fetchAudit]);

  const summary = report?.summary;
  const findings = useMemo(() => {
    if (!report?.findings) return [];
    let rows = [...report.findings];
    if (severityFilter === 'issues') {
      rows = rows.filter((r) => r.count > 0);
    } else if (severityFilter !== 'all') {
      rows = rows.filter((r) => r.severity === severityFilter);
    }
    rows.sort((a, b) => {
      // Issues first, then by severity, then by count desc.
      const ah = a.count > 0 ? 0 : 1;
      const bh = b.count > 0 ? 0 : 1;
      if (ah !== bh) return ah - bh;
      const ad = (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9);
      if (ad !== 0) return ad;
      return b.count - a.count;
    });
    return rows;
  }, [report, severityFilter]);

  return (
    <div data-testid="financial-integrity-panel">
      <div className="flex items-start justify-between mb-4">
        <p className="text-sm text-gray-500 max-w-2xl">
          Read-only scanner. Reconciles orders, shipments, refunds, payouts and
          subscription invoices against each other. Surfaces orphans, parent/child
          drift, lifecycle stalls and revenue-recognition mismatches with their
          financial impact. See{' '}
          <code className="bg-gray-100 px-1 rounded">REVENUE_RECOGNITION_RULES.md</code> for
          the recognition contract.
        </p>
        <Button
          variant="outline"
          onClick={fetchAudit}
          disabled={loading}
          data-testid="financial-integrity-refresh-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Re-run audit
        </Button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <SummaryTile
          testid="fi-tile-total-orders"
          label="Orders"
          value={report?.total_orders ?? '—'}
        />
        <SummaryTile
          testid="fi-tile-total-shipments"
          label="Shipments"
          value={report?.total_shipments ?? '—'}
        />
        <SummaryTile
          testid="fi-tile-total-issues"
          label="Issues found"
          value={summary?.total_issues ?? '—'}
          accent={(summary?.total_issues ?? 0) > 0 ? 'text-amber-600' : 'text-emerald-600'}
        />
        <SummaryTile
          testid="fi-tile-critical"
          label="Critical"
          value={summary?.critical_count ?? '—'}
          accent={(summary?.critical_count ?? 0) > 0 ? 'text-rose-600' : 'text-emerald-600'}
        />
        <SummaryTile
          testid="fi-tile-gmv-impact"
          label="GMV impact"
          value={summary ? formatRupees(summary.total_gmv_impact) : '—'}
          accent={(summary?.total_gmv_impact ?? 0) > 0 ? 'text-rose-600' : ''}
        />
        <SummaryTile
          testid="fi-tile-payout-impact"
          label="Payout impact"
          value={summary ? formatRupees(summary.total_payout_impact) : '—'}
          accent={(summary?.total_payout_impact ?? 0) > 0 ? 'text-rose-600' : ''}
        />
      </div>

      {/* Severity filter chips */}
      <div className="flex gap-2 mb-3" data-testid="fi-filter-chips">
        {['issues', 'all', 'critical', 'high', 'medium', 'low'].map((sev) => (
          <Button
            key={sev}
            size="sm"
            variant={severityFilter === sev ? 'default' : 'outline'}
            onClick={() => setSeverityFilter(sev)}
            data-testid={`fi-chip-${sev}`}
          >
            {sev === 'issues' ? 'With issues only' : sev}
          </Button>
        ))}
      </div>

      {/* Findings table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ListChecks className="h-4 w-4 text-indigo-600" />
            Findings ({findings.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table data-testid="fi-findings-table">
            <TableHeader>
              <TableRow>
                <TableHead>Severity</TableHead>
                <TableHead>Check</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Count</TableHead>
                <TableHead className="text-right">GMV impact</TableHead>
                <TableHead className="text-right">Payout impact</TableHead>
                <TableHead>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {findings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-gray-500 py-8">
                    {loading ? 'Running audit…' : 'No findings match the current filter.'}
                  </TableCell>
                </TableRow>
              ) : (
                findings.map((f) => (
                  <TableRow
                    key={f.code}
                    className={f.count > 0 ? '' : 'opacity-50'}
                    data-testid={`fi-row-${f.code}`}
                  >
                    <TableCell><SeverityBadge severity={f.severity} /></TableCell>
                    <TableCell>
                      <div className="font-medium">{f.title}</div>
                      <div className="text-xs text-gray-400 font-mono">{f.code}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs capitalize">{f.category}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {f.count === 0 ? (
                        <span className="text-emerald-600">0</span>
                      ) : (
                        <span className="text-rose-600 font-bold">{f.count}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono">{formatRupees(f.gmv_impact)}</TableCell>
                    <TableCell className="text-right font-mono">{formatRupees(f.payout_impact)}</TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={f.count === 0}
                        onClick={() => setDrilldown(f)}
                        data-testid={`fi-drilldown-${f.code}`}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Drilldown dialog */}
      <Dialog open={!!drilldown} onOpenChange={(open) => !open && setDrilldown(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="fi-drilldown-dialog">
          {drilldown && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <SeverityBadge severity={drilldown.severity} />
                  {drilldown.title}
                </DialogTitle>
                <DialogDescription className="font-mono text-xs">{drilldown.code}</DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-4 gap-3 my-3 text-sm">
                <div>
                  <div className="text-gray-500">Count</div>
                  <div className="font-bold text-rose-600">{drilldown.count}</div>
                </div>
                <div>
                  <div className="text-gray-500">GMV impact</div>
                  <div className="font-mono">{formatRupees(drilldown.gmv_impact)}</div>
                </div>
                <div>
                  <div className="text-gray-500">Payout impact</div>
                  <div className="font-mono">{formatRupees(drilldown.payout_impact)}</div>
                </div>
                <div>
                  <div className="text-gray-500">GST impact</div>
                  <div className="font-mono">{formatRupees(drilldown.gst_impact)}</div>
                </div>
              </div>

              {drilldown.fix_hint && (
                <div className="bg-indigo-50 border border-indigo-200 rounded p-3 text-sm text-indigo-900 my-2">
                  <strong>Fix hint:</strong> {drilldown.fix_hint}
                </div>
              )}

              <h4 className="font-semibold mt-3 mb-1 text-sm">Affected records (sample, up to 50)</h4>
              <div className="bg-gray-50 rounded p-2 max-h-64 overflow-y-auto">
                <table className="w-full text-xs font-mono">
                  <tbody>
                    {(drilldown.affected_ids || []).map((row, idx) => (
                      <tr key={idx} className="border-b border-gray-200">
                        {Object.entries(row).map(([k, v]) => (
                          <td key={k} className="px-2 py-1">
                            <span className="text-gray-400">{k}=</span>
                            <span className="text-gray-700">{String(v)}</span>
                          </td>
                        ))}
                      </tr>
                    ))}
                    {(drilldown.affected_ids || []).length === 0 && (
                      <tr><td className="text-gray-400 px-2 py-2">No sample IDs returned.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDrilldown(null)} data-testid="fi-drilldown-close">
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
